/**
 * Web-local game loaders. Inlining these in the web app (vs going
 * through @tt/games-registry/client) keeps Vite's relative-path
 * resolution simple and unambiguous in production builds.
 */
import type { ClientGame, GameManifest } from '@tt/types';

type Loader = () => Promise<{ default: ClientGame }>;

/**
 * Lazy per-game manifest loaders (wave-0 shell foundations). Each
 * manifest.json stays out of the initial bundle and loads alongside the
 * game's client chunk; GameStage reads `controlScheme` / `orientation`
 * from it to decide whether to mount the shell controls layer.
 */
const MANIFEST_LOADERS = import.meta.glob('../../../../games/*/manifest.json') as Record<string, () => Promise<{ default: GameManifest }>>;

export async function loadGameManifest(gameId: string): Promise<GameManifest | null> {
  const loader = MANIFEST_LOADERS[`../../../../games/${gameId}/manifest.json`];
  if (!loader) return null;
  try {
    const mod = await loader();
    return mod.default ?? null;
  } catch (e) {
    console.error('[loaders] failed to load manifest', gameId, e);
    return null;
  }
}

export const GAME_LOADERS: Record<string, Loader> = {
  'tap-race': () => import('../../../../games/tap-race/client/index'),
  'witch-switch': () => import('../../../../games/witch-switch/client/index'),
  'tarot-tap': () => import('../../../../games/tarot-tap/client/index'),
  'spice-rush': () => import('../../../../games/spice-rush/client/index'),
  'trace-race': () => import('../../../../games/trace-race/client/index'),
  'ouija-roulette': () => import('../../../../games/ouija-roulette/client/index'),
  'quick-draw': () => import('../../../../games/quick-draw/client/index'),
  'one-of-us': () => import('../../../../games/one-of-us/client/index'),
  'coin-conveyer': () => import('../../../../games/coin-conveyer/client/index'),
  'hexagon-heat': () => import('../../../../games/hexagon-heat/client/index'),
  'last-lantern': () => import('../../../../games/last-lantern/client/index'),
  'lumber-tumble': () => import('../../../../games/lumber-tumble/client/index'),
  'climb-spire': () => import('../../../../games/climb-spire/client/index'),
  'burnstile': () => import('../../../../games/burnstile/client/index'),
  'stick-brawl': () => import('../../../../games/stick-brawl/client/index'),
  'draft-duel': () => import('../../../../games/draft-duel/client/index'),
  'cipher-cellar': () => import('../../../../games/cipher-cellar/client/index'),
  'quill-race': () => import('../../../../games/quill-race/client/index'),
  'sky-sieve': () => import('../../../../games/sky-sieve/client/index'),
  'trapline': () => import('../../../../games/trapline/client/index'),
  'splatter-square': () => import('../../../../games/splatter-square/client/index'),
  'plinko-tavern': () => import('../../../../games/plinko-tavern/client/index'),
  'pirate-pulley': () => import('../../../../games/pirate-pulley/client/index'),
  'rope-walk': () => import('../../../../games/rope-walk/client/index'),
  'cellar-sweep': () => import('../../../../games/cellar-sweep/client/index'),
  'phantom-pursuit': () => import('../../../../games/phantom-pursuit/client/index'),
  'heist-heist': () => import('../../../../games/heist-heist/client/index'),
  'banner-bash': () => import('../../../../games/banner-bash/client/index'),
  'stack-smack': () => import('../../../../games/stack-smack/client/index'),
  'relay-forge': () => import('../../../../games/relay-forge/client/index'),
};

export async function loadGame(gameId: string): Promise<ClientGame | null> {
  const loader = GAME_LOADERS[gameId];
  if (!loader) {
    // If the lobby/server knows a newly deployed game but this tab's old
    // JS bundle does not, `loader` is missing and GameStage would show
    // "Unknown game" forever. Force one guarded refresh to pick up the
    // latest index.html + loader map.
    if (typeof window !== 'undefined') {
      const key = `__tt_unknown_game_reload_${gameId}`;
      const last = Number(sessionStorage.getItem(key) ?? 0);
      const now = Date.now();
      if (now - last > 30_000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
      }
    }
    return null;
  }
  try {
    const mod = await loader();
    return mod.default;
  } catch (e) {
    console.error('[loaders] failed to load game', gameId, e);
    // A "Failed to fetch dynamically imported module" almost always means
    // the page has a stale chunk hash from a deploy that already shipped
    // newer assets. Force a one-time hard refresh so the user picks up
    // the new index.html with the correct chunk references.
    const msg = e instanceof Error ? e.message : String(e);
    const isStaleChunk = /failed to (fetch|load) dynamically imported module|loading chunk \d+ failed|mime type.*(text\/html|javascript)|strict mime/i.test(msg);
    if (isStaleChunk && typeof window !== 'undefined') {
      // Time-based dedupe (not session-once): a single reload almost always
      // fixes a stale chunk after a deploy, but if the user keeps the tab
      // open across MULTIPLE deploys we want to reload again — just not
      // within 30s of the last attempt (loop guard).
      const key = '__tt_chunk_reload_at';
      const last = Number(sessionStorage.getItem(key) ?? 0);
      const now = Date.now();
      if (now - last > 30_000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
      }
    }
    return null;
  }
}
